import React, { useEffect, useState } from 'react';
import {
  Box,
  CallSplit,
  ChevronRight,
  Divider,
  ExpandMore,
  Grid,
  IconButton,
  makeStyles,
  MoreVert,
  StatusDisplay,
  Tooltip,
  Typography,
} from 'housecall-ui';
import {
  QUESTION_TYPE,
  QuestionType,
  REMINDER_TYPE,
  ReminderType,
  RequestGuideAnswer,
  RequestGuideNode,
  RequestGuideNodeQuestion,
} from 'resources/CustomerIntakeFlow/requestGuide';
import { v4 } from 'uuid';

import { RequestGuideNodeMenuAction } from 'components/CustomerIntakeSettings/requestGuide/RequestGuideCard';
import { RequestGuideCardList } from 'components/CustomerIntakeSettings/requestGuide/RequestGuideCardList';
import { RequestGuideChildrenWrapper } from 'components/CustomerIntakeSettings/requestGuide/RequestGuideChildrenWrapper';
import { QuestionTypes } from 'components/CustomerIntakeSettings/requestGuide/types';

import { CloneOptionType } from './RequestGuidePage';

const questionTypeMap: Record<QuestionTypes, string> = {
  'single-select': 'Single choice',
  'multiple-select': 'Multiple choice',
  text: 'Text',
};

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
  },
  statusIcon: {
    marginLeft: '-4px !important',
  },
  statusLabel: {
    color: theme.palette.text.secondary,
  },
  icon: {
    transform: 'rotate(90deg)',
  },
  optionsButton: {
    alignSelf: 'flex-start',
  },
}));

export type QuestionContentProps = {
  question: RequestGuideNodeQuestion;
  branches: number;
  showBranching: boolean;
  onOptionsOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onBranchToggle: () => void;
  onMenuAction: (arg: RequestGuideNodeMenuAction, node: RequestGuideNode, options?: CloneOptionType) => void;
  isHcpAssistImpersonated?: boolean;
  editable: boolean;
};

export const replaceDash = (text: string) => text.replace(/[_-]/g, '').toLowerCase();

function getEmptyNode(type: QuestionType | ReminderType, parentUid: string, answerUid: string): RequestGuideNode {
  return type === QUESTION_TYPE
    ? {
        type: QUESTION_TYPE,
        parentId: parentUid,
        parentAnswerId: answerUid,
        uid: replaceDash(v4()),
        childNodes: {},
        questionType: 'single-select',
        answerType: 'custom',
        answers: [
          {
            uid: replaceDash(v4()),
            value: '',
          },
        ],
        question: '',
        reminder: '',
      }
    : {
        type: REMINDER_TYPE,
        title: '',
        note: '',
        uid: replaceDash(v4()),
        parentId: parentUid,
        parentAnswerId: answerUid,
      };
}

export const QuestionContent = ({
  question,
  onOptionsOpen,
  branches,
  onMenuAction,
  showBranching,
  onBranchToggle,
  isHcpAssistImpersonated,
  editable,
}: QuestionContentProps) => {
  const classes = useStyles();
  const [isExpanded, setExpanded] = useState(false);
  const childNodes = question.childNodes || {};

  const answers = question.answers?.map((answer) => ({ ...answer, uid: replaceDash(answer.uid) })) ?? [];

  const answersBranched = answers
    ?.filter((answer: RequestGuideAnswer) => (childNodes[answer.uid]?.length ?? 0) > 0)
    .map(({ uid }) => [uid, childNodes[uid]!] as const);

  const answersIdsWithBranch = answersBranched.map(([key]) => key);

  const answersNotBranched = answers?.filter(
    (answer: RequestGuideAnswer) => !answersIdsWithBranch.includes(answer.uid)
  );

  const showChildNodes = isExpanded && answersBranched.length > 0 && answers.length;
  const showDraftBranches = isExpanded && answers.length && (showBranching || answersBranched.length > 0);

  useEffect(() => setExpanded(showBranching), [showBranching]);

  const onExpandToggle = () => {
    setExpanded((state) => !state);
    onBranchToggle();
  };

  return (
    <>
      <Grid container wrap="nowrap">
        {question.questionType === 'single-select' ? (
          <Grid item xs="auto">
            {branches === 0 && !showBranching ? (
              <Box p={1} pt={4}>
                <Tooltip title="Create branch">
                  <IconButton onClick={onExpandToggle} data-testid="request-create-branch">
                    <CallSplit className={classes.icon} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box p={1} pt={2}>
                <IconButton onClick={onExpandToggle} data-testid="request-show-branches">
                  <Box display="flex" flexDirection="column">
                    <Typography align="center" variant="caption">
                      {branches}
                    </Typography>
                    {isExpanded ? <ExpandMore /> : <ChevronRight />}
                  </Box>
                </IconButton>
              </Box>
            )}
          </Grid>
        ) : (
          <Box ml={8} />
        )}
        <Grid item className={classes.content}>
          <Box display="flex" width="100%" justifyContent="space-between" pt={2} pb={2}>
            <Box>
              <StatusDisplay
                classes={{
                  icon: classes.statusIcon,
                  label: classes.statusLabel,
                }}
                status=""
                color="blue"
                label={`Question - ${questionTypeMap[question.questionType]}`}
              />
              <Typography gutterBottom data-testid="customerIntake__requestGuideTitle" variant="subtitle1">
                {question.question}
              </Typography>
              <Typography
                gutterBottom
                data-testid="customerIntake__requestGuideSubtitle"
                color="secondary"
                variant="subtitle2"
              >
                {answers.map((answer: RequestGuideAnswer) => answer.value).join(', ') ?? ''}
              </Typography>
              {question.reminder && (
                <Typography data-testid="customerIntake__requestGuideCaption" variant="caption" color="textPrimary">
                  {question.reminder}
                </Typography>
              )}
            </Box>
            {editable && (
              <IconButton className={classes.optionsButton} data-testid="request-card-options" onClick={onOptionsOpen}>
                <MoreVert />
              </IconButton>
            )}
          </Box>
          {showChildNodes
            ? answersBranched.map(([answerUid, value], index) => (
                <Box mr={2} mb={3} key={answerUid}>
                  <RequestGuideChildrenWrapper
                    answers={answers}
                    isFirst={index === 0}
                    isLast={answersBranched.length - 1 === index}
                    currentAnswer={answers.find(({ uid }) => uid === replaceDash(answerUid))}
                    onAdd={(type) => onMenuAction('add', getEmptyNode(type, question.uid, answerUid))}
                    onClone={(data, options) => data && onMenuAction('clone', data, options)}
                    data={value}
                    isHcpAssistImpersonated={!!isHcpAssistImpersonated}
                    editable={editable}
                  >
                    <RequestGuideCardList
                      data={value}
                      onMenuAction={onMenuAction}
                      isHcpAssistImpersonated={!!isHcpAssistImpersonated}
                      editable={editable}
                    />
                  </RequestGuideChildrenWrapper>
                </Box>
              ))
            : null}

          {showDraftBranches
            ? answersNotBranched?.map((answer, index) => (
                <Box mr={2} mb={3} key={answer.uid}>
                  <RequestGuideChildrenWrapper
                    onAdd={(type) => onMenuAction('add', getEmptyNode(type, question.uid, answer.uid))}
                    answers={answers}
                    isFirst={answersBranched.length === 0 && index === 0}
                    isDraft
                    isLast={answersNotBranched.length - 1 === index}
                    currentAnswer={answer}
                    isHcpAssistImpersonated={!!isHcpAssistImpersonated}
                    editable={editable}
                  />
                </Box>
              ))
            : null}
        </Grid>
      </Grid>
      <Divider />
    </>
  );
};
